import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import type { UseNavigateResult } from '@tanstack/react-router';

import { useMe } from '@/api/me';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { useCreateEstate } from '@/features/estates/api/estates';
import { EstateForm } from '@/features/estates/components/estate-form';
import { type EstateSchemaType, getCreateEstateSchema } from '@/features/estates/schemas/estate';
import type { Estate } from '@/features/estates-property/api/estate';
import { getProjectEstatesOptions } from '@/features/projects/api/project-estates';

import { ModalBase } from './modals-base';

export interface CopyEstateModalProps {
  estate: Estate;
  navigate: UseNavigateResult<string>;
}

export const CopyEstateModal: React.FC<CopyEstateModalProps> = ({ estate, navigate }) => {
  const [t] = useTranslation();
  const schema = getCreateEstateSchema();
  const { data: me } = useMe();
  const queryClient = useQueryClient();

  const { mutateAsync: createEstateAsync } = useCreateEstate({
    mutationConfig: {
      onSuccess: ([createdEstate]) => {
        queryClient.invalidateQueries({ queryKey: ['estates'] });
        if (createdEstate.projectId) {
          queryClient.invalidateQueries({
            queryKey: getProjectEstatesOptions({ projectId: createdEstate.projectId, query: {} })
              .queryKey,
          });
        }
        navigate({ to: '/estates/$estateId', params: { estateId: createdEstate.id } });
        toast.success(t('create-estate-success'));
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const methods = useForm<EstateSchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      estateData: {
        name: estate?.name || '',
        propertyType: estate?.estateDetails?.propertyType || '',
        marketType: estate?.estateDetails?.marketType || '',
        tenantId: estate?.tenantId || '',
        projectId: estate?.projectId || '',
        creatorId: me?.id,
        propertyLevel: PROPERTY_LEVELS.OBJECT,
      },
      addressData: {
        ...(estate?.address?.address && {
          addressAutoComplete: estate.address.address,
          address: estate.address.address,
        }),
        postalCode: estate?.address?.postalCode || '',
        city: estate?.address?.city || '',
        ...(estate?.address?.street && { street: estate.address.street }),
        ...(estate?.address?.houseNumber && {
          houseNumber: estate.address.houseNumber,
        }),
        countryId: estate.address?.country?.id || '',
      },
    },
  });

  const handleSubmit = async (args: EstateSchemaType) => {
    await createEstateAsync(args);
  };

  return (
    <ModalBase>
      <FormProvider {...methods}>
        <EstateForm
          label={t('add-new-estate')}
          onSubmit={handleSubmit}
          options={{
            propertyLevelDisabled: true,
            tenantDisabled: true,
          }}
        />
      </FormProvider>
    </ModalBase>
  );
};
